import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { FieldSpec, FrameworkForm } from "./framework-config";
import type { StartInputs } from "@/api/create-types";
import { extractTextFromFile } from "@/api/files";
import { listMarkedFiles, type MarkedFile } from "@/api/projects";
import { startRun } from "@/api/runs";
import { beginAndPostRun } from "./active-runs-store";
import { generationErrorMessage } from "@/api/llm-access";
import { savePdfMeta } from "./lecture/lecture-pdf-store";
import { usePdfIntake } from "./use-pdf-intake";
import { RoundChooser } from "./round-chooser";
import { Alert, Button, Input } from "@/components/ui";

const isPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);

// Separator used to join pasted material with extracted file text. Shared so an
// upload's text chunk can be located and stripped back out when its chip is
// removed.
const SOURCE_SEP = "\n\n---\n\n";

// Frameworks whose backend supports the universal marked-PDF pipeline (they
// have a discussion profile). Only these offer the round chooser on PDF upload;
// others (e.g. language-trainer, user-authored) keep the extract-into-box path.
const MARKING_SLUGS = new Set([
  "learn-a-topic",
  "deep-dive",
  "math-deep-dive",
  "lecture-exercise-recap",
  "master-a-trait",
]);

type FieldValue = string | number | boolean;

function initialState(fields: FieldSpec[]): Record<string, FieldValue> {
  const state: Record<string, FieldValue> = {};
  for (const f of fields) {
    state[f.key] = f.default ?? (f.kind === "toggle" ? false : "");
  }
  return state;
}

/** Assembles the start_inputs dict from form state, applying the include rules:
 *  toggles are always sent as explicit booleans; text/select/source are sent
 *  when non-empty; numbers when provided; `file` fields are skipped (they write
 *  their extracted text into `source_text`). A `source` field IS a real input
 *  (paste + uploaded-file text live in its own key), so it's sent like a
 *  textarea. `language` defaults to "en" but a form's `transform` can override it. */
function buildStartInputs(fields: FieldSpec[], state: Record<string, FieldValue>): StartInputs {
  const out: StartInputs = { language: "en" };
  for (const f of fields) {
    if (f.kind === "file") continue;
    const v = state[f.key];
    if (f.kind === "toggle") {
      out[f.key] = Boolean(v);
    } else if (f.kind === "number") {
      const n = Number(v);
      if (v !== "" && !Number.isNaN(n)) out[f.key] = n;
    } else if (f.kind === "marked-file-picker") {
      // The picker stores a single chosen file_id; the backend takes a list.
      const s = String(v).trim();
      if (s) out[f.key] = [s];
    } else {
      const s = String(v).trim();
      if (s) out[f.key] = s;
    }
  }
  return out;
}

export function SetupForm({
  slug,
  frameworkTitle,
  form,
  submitting,
  serverError,
  onSubmit,
}: {
  slug: string;
  frameworkTitle: string;
  form: FrameworkForm;
  submitting: boolean;
  serverError: string | null;
  onSubmit: (startInputs: StartInputs) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<Record<string, FieldValue>>(() => initialState(form.fields));
  const [localError, setLocalError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileNote, setFileNote] = useState<string | null>(null);
  // Per-field record of files extracted into a textarea, so each shows as a
  // removable chip. `text` is the exact chunk appended to `state[key]`.
  const [uploads, setUploads] = useState<Array<{ key: string; name: string; text: string }>>([]);

  // PDF round system: a picked PDF is NOT extracted into the source box; it
  // opens the three-way chooser (study / mark / build). DOCX/TXT + pasted text
  // keep the plain extract-into-textarea path below.
  const { intake, pick, reset } = usePdfIntake();
  const [roundBusy, setRoundBusy] = useState(false);
  const choosing = Boolean(intake.pdfRef || intake.processing);

  // "Load a marked file" picker: fetch the user's already-marked files once, only
  // when a field needs them. Best-effort — on failure the picker just stays empty.
  const hasMarkedPicker = useMemo(
    () => form.fields.some((f) => f.kind === "marked-file-picker"),
    [form.fields],
  );
  const [markedFiles, setMarkedFiles] = useState<MarkedFile[]>([]);
  const [markedLoading, setMarkedLoading] = useState(false);
  useEffect(() => {
    if (!hasMarkedPicker) return;
    let active = true;
    setMarkedLoading(true);
    listMarkedFiles()
      .then((rows) => {
        if (active) setMarkedFiles(rows);
      })
      .catch(() => {
        /* ignore — picker stays empty */
      })
      .finally(() => {
        if (active) setMarkedLoading(false);
      });
    return () => {
      active = false;
    };
  }, [hasMarkedPicker]);

  const set = (key: string, value: FieldValue) => setState((s) => ({ ...s, [key]: value }));

  const startInputs = useMemo(() => {
    const built = buildStartInputs(form.fields, state);
    return form.transform ? form.transform(built) : built;
  }, [form, state]);

  // A PDF in the picked file list opens the round chooser (first PDF wins);
  // any non-PDF files fall through to the normal extract-into-textarea path.
  const onSourceFiles = (key: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const pdf = arr.find(isPdf);
    // Only marking-capable frameworks open the round chooser for a PDF; others
    // (and all DOCX/TXT) keep the plain extract-into-textarea path.
    if (pdf && MARKING_SLUGS.has(slug)) {
      void pick(pdf);
      return;
    }
    void handleFiles(key, files);
  };

  // Merge the form's other fields (topic, focus, level…) with the PDF refs.
  const pdfStartInputs = (mark: boolean): StartInputs => ({
    ...startInputs,
    source_text: intake.sourceText,
    local_pdf_ref: intake.pdfRef ?? "",
    ...(intake.sourcePdfKey ? { source_pdf_key: intake.sourcePdfKey } : {}),
    discussion: true,
    ...(mark ? { include_marking: true } : {}),
  });

  const startPdfRun = async (mark: boolean) => {
    if (!intake.pdfRef) return;
    setRoundBusy(true);
    setLocalError(null);
    // Build-now is pure generation: the POST blocks for the whole (synchronous)
    // generation, so fire it in the background and land on Create immediately,
    // where a placeholder pill shows "Generating…".
    if (!mark) {
      beginAndPostRun({ frameworkSlug: slug, frameworkTitle, startInputs: pdfStartInputs(false) });
      navigate("/app/create");
      return;
    }
    // Mark-now pauses immediately for marking and needs the run id → await it,
    // then go to the marking step.
    try {
      const run = await startRun(slug, pdfStartInputs(true));
      navigate(`/app/create/run/${run.id}`);
    } catch (e) {
      setLocalError(generationErrorMessage(e, t));
      setRoundBusy(false);
    }
  };

  const studyThisPdf = async () => {
    if (!intake.pdfRef) return;
    setRoundBusy(true);
    try {
      await savePdfMeta({
        ref: intake.pdfRef,
        name: intake.fileName.replace(/\.pdf$/i, "") || t("Untitled PDF"),
        createdAt: Date.now(),
        markCount: 0,
      });
      navigate(`/app/study/${intake.pdfRef}`);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t("Couldn't open the study view."));
      setRoundBusy(false);
    }
  };

  // Extract one or more uploaded files and append their text to the target
  // field's value (paste + upload coexist). Used by both the `source` field
  // (targets its own key) and the legacy `file` field (targets source_text).
  const handleFiles = async (key: string, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setLocalError(null);
    try {
      const added: Array<{ name: string; text: string }> = [];
      for (const file of Array.from(files)) {
        const { text } = await extractTextFromFile(file);
        if (text.trim()) added.push({ name: file.name, text });
      }
      if (added.length) {
        setState((s) => ({
          ...s,
          [key]: [String(s[key] ?? ""), ...added.map((a) => a.text)]
            .filter(Boolean)
            .join(SOURCE_SEP),
        }));
        setUploads((u) => [...u, ...added.map((a) => ({ key, name: a.name, text: a.text }))]);
        setFileNote(null);
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t("Couldn't read that file."));
    } finally {
      setUploading(false);
    }
  };

  // Drop an uploaded file's chip and strip its extracted text from the target
  // field. Best-effort: if the user hand-edited the box so the chunk no longer
  // matches, the chip is still removed and the text left untouched.
  const removeUpload = (idx: number) => {
    const entry = uploads[idx];
    if (!entry) return;
    setState((s) => {
      const chunks = String(s[entry.key] ?? "").split(SOURCE_SEP);
      const at = chunks.indexOf(entry.text);
      if (at === -1) return s;
      chunks.splice(at, 1);
      return { ...s, [entry.key]: chunks.join(SOURCE_SEP) };
    });
    setUploads((u) => u.filter((_, i) => i !== idx));
  };

  // Removable chips for files extracted into a given target field.
  const uploadChips = (targetKey: string) => {
    const rows = uploads
      .map((u, i) => ({ u, i }))
      .filter(({ u }) => u.key === targetKey);
    if (rows.length === 0) return null;
    return (
      <ul className="mt-2 space-y-1">
        {rows.map(({ u, i }) => (
          <li
            key={i}
            className="flex items-center gap-2 rounded-lg bg-brand-50/60 px-2.5 py-1.5 text-xs"
          >
            <span className="flex-1 truncate text-ink-soft" title={u.name}>
              📄 {u.name}
            </span>
            <button
              type="button"
              onClick={() => removeUpload(i)}
              aria-label={t("Remove {{name}}", { name: u.name })}
              className="shrink-0 text-ink-faint hover:text-rose-600"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (uploading) return;
    const err = form.validate?.(startInputs) ?? null;
    if (err) {
      setLocalError(err);
      return;
    }
    onSubmit(startInputs);
  };

  if (form.unavailableNote) {
    return <Alert tone="info">{t(form.unavailableNote)}</Alert>;
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {(localError || serverError) && <Alert>{localError ?? serverError}</Alert>}

      {choosing && (
        <RoundChooser
          intake={intake}
          busy={roundBusy}
          onStudy={() => void studyThisPdf()}
          onMark={() => void startPdfRun(true)}
          onGenerate={() => void startPdfRun(false)}
          onCancel={reset}
        />
      )}

      {form.fields.map((f) => {
        if (f.kind === "toggle") {
          return (
            <label key={f.key} className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={Boolean(state[f.key])}
                onChange={(e) => set(f.key, e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-black/20 text-brand-600 focus:ring-brand-400"
              />
              <span>
                <span className="block text-sm font-medium text-ink">{t(f.label)}</span>
                {f.hint && <span className="block text-xs text-ink-faint">{t(f.hint)}</span>}
              </span>
            </label>
          );
        }
        if (f.kind === "marked-file-picker") {
          const empty = !markedLoading && markedFiles.length === 0;
          return (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t(f.label)}</span>
              <select
                value={String(state[f.key] ?? "")}
                onChange={(e) => set(f.key, e.target.value)}
                disabled={markedLoading || empty}
                className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-60"
              >
                <option value="">
                  {markedLoading
                    ? t("Loading your marked files…")
                    : empty
                      ? t("No marked files yet — mark one in a project first")
                      : t("None")}
                </option>
                {markedFiles.map((mf) => (
                  <option key={mf.file_id} value={mf.file_id}>
                    {mf.filename} · {mf.project_title} (
                    {t("{{count}} marks", { count: mf.deep_count + mf.lookup_count })})
                  </option>
                ))}
              </select>
              {f.hint && <span className="mt-1 block text-xs text-ink-faint">{t(f.hint)}</span>}
            </label>
          );
        }
        if (f.kind === "select") {
          return (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t(f.label)}</span>
              <select
                value={String(state[f.key] ?? "")}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.label)}
                  </option>
                ))}
              </select>
              {f.hint && <span className="mt-1 block text-xs text-ink-faint">{t(f.hint)}</span>}
            </label>
          );
        }
        if (f.kind === "file") {
          return (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t(f.label)}</span>
              <input
                type="file"
                accept={f.accept}
                multiple
                disabled={uploading}
                onChange={(e) => {
                  void handleFiles("source_text", e.target.files);
                  e.target.value = "";
                }}
                className="block w-full text-sm text-ink-soft file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
              />
              {uploading && <span className="mt-1 block text-xs text-brand-600">{t("Reading file…")}</span>}
              {uploadChips("source_text")}
              {fileNote && !uploading && (
                <span className="mt-1 block text-xs text-emerald-600">{fileNote}</span>
              )}
              {f.hint && <span className="mt-1 block text-xs text-ink-faint">{t(f.hint)}</span>}
            </label>
          );
        }
        if (f.kind === "textarea") {
          return (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t(f.label)}</span>
              <textarea
                value={String(state[f.key] ?? "")}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder ? t(f.placeholder) : undefined}
                rows={5}
                className="w-full resize-y rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint/70 focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              {f.hint && <span className="mt-1 block text-xs text-ink-faint">{t(f.hint)}</span>}
            </label>
          );
        }
        // Unified source-input: paste material AND/OR upload file(s) into the
        // same field. Shared by every framework that takes source material so
        // upload UX is identical everywhere (fixes the per-framework divergence).
        if (f.kind === "source") {
          const accept = f.accept ?? ".pdf,.txt,.md,.docx";
          return (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t(f.label)}</span>
              <textarea
                value={String(state[f.key] ?? "")}
                onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder ? t(f.placeholder) : undefined}
                rows={5}
                className="w-full resize-y rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint/70 focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <label className="cursor-pointer rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100">
                  {t("Upload file(s)")}
                  <input
                    type="file"
                    accept={accept}
                    multiple
                    disabled={uploading || choosing}
                    onChange={(e) => {
                      onSourceFiles(f.key, e.target.files);
                      e.target.value = "";
                    }}
                    className="hidden"
                  />
                </label>
                <span className="text-xs text-ink-faint">
                  {f.hint ? t(f.hint) : t("PDF — read & mark it, or build straight from it. DOCX/TXT read into the box.")}
                </span>
              </div>
              {uploading && <span className="mt-1 block text-xs text-brand-600">{t("Reading file…")}</span>}
              {uploadChips(f.key)}
              {fileNote && !uploading && (
                <span className="mt-1 block text-xs text-emerald-600">{fileNote}</span>
              )}
            </label>
          );
        }
        return (
          <Input
            key={f.key}
            label={t(f.label)}
            value={String(state[f.key] ?? "")}
            onChange={(e) => set(f.key, e.target.value)}
            placeholder={f.placeholder ? t(f.placeholder) : undefined}
            hint={f.hint ? t(f.hint) : undefined}
            type={f.kind === "number" ? "number" : "text"}
          />
        );
      })}

      {!choosing && (
        <Button
          type="submit"
          size="lg"
          loading={submitting || uploading}
          disabled={uploading}
          className="w-full sm:w-auto"
        >
          {t("Generate set")}
        </Button>
      )}
    </form>
  );
}
